import { Injectable } from '@angular/core';
import {
  GroundworksTableData,
  TableGroup,
} from '@groundworks/ngx-groundworks-ui';
import { QuoteInfo, QuoteProductInfo } from '../sdk/sdk';

@Injectable({
  providedIn: 'root',
})
export class QuoteTableBuilderService {
  buildTableData(quote: QuoteInfo): GroundworksTableData {
    const groups: TableGroup[] = [];

    this.addQuotedProductsGroup(groups, quote);
    this.addRecommendedProductsGroup(groups, quote);
    this.addRecurringProductsGroup(groups, quote);

    const hasDiscount = !!(quote.discounts && quote.discounts.length > 0);
    const hasTax = !!quote.tax;
    const hasBoth = hasDiscount && hasTax;
    const hasEither = hasDiscount || hasTax;

    if (hasEither) {
      this.addProductTotalRow(groups, quote);
    }

    this.addDiscountsGroup(groups, quote);

    if (hasBoth) {
      this.addSubTotalRow(groups, quote);
    }

    this.addTaxGroup(groups, quote);
    this.addGrandTotalRow(groups, quote);

    return {
      caption: 'Products',
      className: 'pricing-table',
      columns: [
        { label: 'Product', dataType: 'text' as const },
        { label: 'Sold Amt.', dataType: 'text' as const },
        { label: 'Sold Rate', dataType: 'currency' as const },
        { label: 'Sold Price', dataType: 'currency' as const },
        { label: 'Par Price', dataType: 'currency' as const },
      ],
      groups,
    };
  }

  private addGrandTotalRow(groups: TableGroup[], quote: QuoteInfo) {
    this.addTotalRow(
      groups,
      'Total',
      'total',
      quote.soldTotal ?? 0,
      quote.parTotal ?? 0,
      'total-row'
    );
  }

  private addSubTotalRow(groups: TableGroup[], quote: QuoteInfo) {
    this.addTotalRow(
      groups,
      'Subtotal',
      'subtotal',
      quote.subTotal ?? 0,
      quote.parSubTotal ?? 0,
      'subtotal-row'
    );
  }

  private addProductTotalRow(groups: TableGroup[], quote: QuoteInfo) {
    this.addTotalRow(
      groups,
      'Products Total',
      'subtotal',
      quote.productTotal ?? 0,
      quote.parSubTotal ?? 0
    );
  }

  private addTotalRow(
    groups: TableGroup[],
    label: string,
    type: 'total' | 'subtotal',
    soldValue: number,
    parValue: number,
    additionalClass?: string
  ) {
    const className = additionalClass ? `fw-bold ${additionalClass}` : 'fw-bold';
    groups.push({
      label: '',
      rows: [
        {
          type,
          cells: [
            { value: label, isHeader: true, colspan: 3, className },
            { value: soldValue, isHeader: true, className: 'fw-bold' },
            { value: parValue, isHeader: true, className: 'fw-bold' },
          ],
        },
      ],
    });
  }

  private addTaxGroup(groups: TableGroup[], quote: QuoteInfo) {
    if (!quote.tax) {
      return;
    }

    const taxLabel = `${quote.tax.taxTypeName || 'Tax'} (${(quote.tax.rate * 100).toFixed(2)}%)`;
    this.addOptionalDataGroup(
      groups,
      'Tax',
      taxLabel,
      quote.tax.amount ?? 0,
      null
    );
  }

  private addDiscountsGroup(groups: TableGroup[], quote: QuoteInfo) {
    if (!quote.discounts || quote.discounts.length === 0) {
      return;
    }

    // Create header row for Discounts section
    const rows: GroundworksTableData['rows'] = [
      {
        type: 'data' as const,
        cells: [
          {
            value: 'Discounts',
            isHeader: true,
            colspan: 5,
            className: 'bg-light',
          },
        ],
      },
    ];

    // Add a row for each discount
    for (const discount of quote.discounts) {
      const discountLabel = discount.discountTypeName || 'Discount';
      const discountAmount = -Math.abs(discount.amount);
      rows.push({
        type: 'data' as const,
        cells: [
          { value: discountLabel },
          { value: '' },
          { value: '' },
          { value: discountAmount },
          { value: '' },
        ],
      });
    }

    groups.push({
      label: '',
      rows,
    });
  }

  private addOptionalDataGroup(
    groups: TableGroup[],
    headerLabel: string,
    dataLabel: string | null,
    soldValue: number | null,
    parValue: number | null
  ) {
    const rows: GroundworksTableData['rows'] = [
      {
        type: 'data' as const,
        cells: [
          {
            value: headerLabel,
            isHeader: true,
            colspan: 5,
            className: 'bg-light',
          },
        ],
      },
    ];

    if (dataLabel !== null) {
      rows.push({
        type: 'data' as const,
        cells: [
          { value: dataLabel },
          { value: '' },
          { value: '' },
          { value: soldValue ?? 0 },
          { value: parValue ?? '' },
        ],
      });
    }

    groups.push({
      label: '',
      rows,
    });
  }

  private addRecurringProductsGroup(groups: TableGroup[], quote: QuoteInfo) {
    this.addProductGroup(
      groups,
      'Recurring Products',
      quote.recurringProducts
    );
  }

  private addRecommendedProductsGroup(groups: TableGroup[], quote: QuoteInfo) {
    this.addProductGroup(
      groups,
      'Recommended Products',
      quote.recommendedProducts,
      'text-muted'
    );
  }

  private addQuotedProductsGroup(groups: TableGroup[], quote: QuoteInfo) {
    this.addProductGroup(groups, 'Quoted Products', quote.quotedProducts);
  }

  private addProductGroup(
    groups: TableGroup[],
    headerLabel: string,
    products: QuoteProductInfo[] | undefined,
    cellClassName?: string
  ) {
    const rows: GroundworksTableData['rows'] = [
      {
        type: 'data' as const,
        cells: [
          {
            value: headerLabel,
            isHeader: true,
            colspan: 5,
            className: 'bg-light',
          },
        ],
      },
    ];

    if (products && products.length > 0) {
      rows.push(
        ...products.map((product) => ({
          type: 'data' as const,
          cells: [
            {
              value: product.productName || 'Unknown Product',
              className: cellClassName,
              // Add sticky note icon with tooltip if product has notes
              ...(product.notes &&
                product.notes.trim() !== '' && {
                  icon: {
                    class: 'bi bi-sticky text-primary',
                    tooltip: product.notes,
                    ariaLabel: `Notes for ${product.productName || 'product'}`,
                    position: 'after' as const,
                    tooltipPlacement: 'top' as const,
                  },
                }),
            },
            {
              value:
                product.quantity +
                ' ' +
                (product.product?.unitOfMeasure || ''),
              className: cellClassName,
            },
            { value: product.unitAmount, className: cellClassName },
            { value: product.subtotal, className: cellClassName },
            {
              value: (product.product?.parPrice ?? 0) * product.quantity,
              className: cellClassName,
            },
          ],
        }))
      );

      groups.push({
        label: '',
        rows,
      });
    }
  }
}
